import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, Select,
  MenuItem, FormControl, InputLabel, Skeleton, Pagination, Stack,
  InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, Security as SecurityIcon, FileDownload as DownloadIcon } from '@mui/icons-material';
import { analyticsAPI } from '../services/api';

const DARK_BLUE = '#005B8F';
const BLUE = '#00A8E8';

const ACTION_COLORS = {
  'auth:login':         { bg: '#dcfce7', color: '#16a34a' },
  'auth:login_failed':  { bg: '#fee2e2', color: '#dc2626' },
  'patient:view':       { bg: '#dbeafe', color: '#1d4ed8' },
  'patient:search':     { bg: '#e0f2fe', color: '#0369a1' },
  'patient:create':     { bg: '#d1fae5', color: '#065f46' },
  'patient:pii_access': { bg: '#fef3c7', color: '#92400e' },
  'session:start':      { bg: '#ede9fe', color: '#5b21b6' },
  'vitals:record':      { bg: '#f3e8ff', color: '#6b21a8' },
  'report:download':    { bg: '#fce7f3', color: '#9d174d' },
  'report:doctor_note': { bg: '#fff7ed', color: '#9a3412' },
  'family:add':         { bg: '#ecfdf5', color: '#065f46' },
  'fingerprint:enroll': { bg: '#f0fdf4', color: '#14532d' },
};

const ACTION_OPTIONS = [
  '', 'auth:login', 'auth:login_failed', 'patient:view', 'patient:search',
  'patient:create', 'patient:pii_access', 'session:start', 'vitals:record',
  'report:download', 'report:doctor_note', 'family:add', 'fingerprint:enroll',
];

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);

  const [actionFilter, setActionFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userIdSearch, setUserIdSearch] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (actionFilter) params.action = actionFilter;
      if (startDate)    params.startDate = startDate;
      if (endDate)      params.endDate = endDate;
      if (userIdSearch) params.userId = userIdSearch;
      const res = await analyticsAPI.getAuditLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, startDate, endDate, userIdSearch]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleClear = () => {
    setActionFilter('');
    setStartDate('');
    setEndDate('');
    setUserIdSearch('');
    setPage(1);
  };

  const chipFor = (action) => {
    const cfg = ACTION_COLORS[action] || { bg: '#f1f5f9', color: '#475569' };
    return (
      <Chip
        label={action}
        size="small"
        sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: cfg.bg, color: cfg.color, border: 'none' }}
      />
    );
  };

  const fmtTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'medium' });
  };

  const exportCsv = async () => {
    try {
      const params = { page: 1, limit: 5000 };
      if (actionFilter) params.action = actionFilter;
      if (startDate)    params.startDate = startDate;
      if (endDate)      params.endDate = endDate;
      if (userIdSearch) params.userId = userIdSearch;
      const res = await analyticsAPI.getAuditLogs(params);
      const rows = res.data.logs || [];
      const header = ['Timestamp', 'Action', 'Role', 'User ID', 'Resource Type', 'Resource ID', 'IP Address', 'Endpoint', 'Method'];
      const csv = [
        header.join(','),
        ...rows.map(l => [
          fmtTime(l.timestamp), l.action, l.role || '', l.userId || '',
          l.resourceType || '', l.resourceId || '', l.ipAddress || '',
          `"${(l.endpoint || '').replace(/"/g, '""')}"`, l.method || ''
        ].join(','))
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silent — user will notice no download
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SecurityIcon sx={{ color: BLUE, fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: DARK_BLUE }}>Audit Log</Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {total.toLocaleString()} events — 7-year retention
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined" size="small" startIcon={<DownloadIcon />}
          onClick={exportCsv}
          sx={{ borderColor: BLUE, color: BLUE, fontWeight: 600, '&:hover': { bgcolor: '#e0f7ff', borderColor: BLUE } }}
        >
          Export CSV
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Action</InputLabel>
              <Select value={actionFilter} label="Action" onChange={e => { setActionFilter(e.target.value); setPage(1); }}>
                <MenuItem value="">All actions</MenuItem>
                {ACTION_OPTIONS.filter(Boolean).map(a => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small" label="From date" type="date"
              value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
            />
            <TextField
              size="small" label="To date" type="date"
              value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
            />
            <TextField
              size="small" label="User ID" placeholder="Filter by user ID"
              value={userIdSearch}
              onChange={e => { setUserIdSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
              sx={{ minWidth: 200 }}
            />
            {(actionFilter || startDate || endDate || userIdSearch) && (
              <Tooltip title="Clear filters">
                <IconButton size="small" onClick={handleClear}><ClearIcon /></IconButton>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: DARK_BLUE, bgcolor: '#f0f9ff', fontSize: '0.8rem' } }}>
                <TableCell>Timestamp</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Endpoint</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                    No audit logs found for the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map(log => (
                  <TableRow key={log._id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                    <TableCell sx={{ fontSize: '0.78rem', color: '#475569', whiteSpace: 'nowrap' }}>
                      {fmtTime(log.timestamp)}
                    </TableCell>
                    <TableCell>{chipFor(log.action)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155' }}>
                        {log.role || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                      {log.resourceType && <span>{log.resourceType}</span>}
                      {log.resourceId && (
                        <Typography variant="caption" display="block" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                          {log.resourceId.slice(-8)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                      {log.ipAddress || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: '#94a3b8', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.endpoint || '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((page - 1) * 50) + 1}–{Math.min(page * 50, total)} of {total.toLocaleString()} events
            </Typography>
            <Stack>
              <Pagination count={pages} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" showFirstButton showLastButton />
            </Stack>
          </Box>
        )}
      </Card>
    </Box>
  );
}
